import { BusinessObjectBase } from './base';
import { latestProcessDesignSchema } from '@schemas/business-objects/latest-process-design';
import type { Tenant } from './tenant';
import type { Id, Uuid } from '../base-types';

export interface LatestProcessDesignData {
  readonly id: Id;
  readonly designUuid?: Uuid;
  readonly name?: string;
  readonly displayName?: string;
  readonly description?: string;
  readonly version?: number;
  readonly suspended?: boolean;
  readonly createdAt?: Date;
  readonly updatedAt?: Date;
  readonly activeInstanceCount?: number;
  readonly errorInstanceCount?: number;
  readonly tenantId: Id;
  readonly tenant?: Tenant;
}

export interface LatestProcessDesign extends Readonly<LatestProcessDesignData> {}
type LatestProcessDesignCreateWithChildrenData =
  Partial<LatestProcessDesignData>;

export class LatestProcessDesign extends BusinessObjectBase {
  static readonly entityName = 'LatestProcessDesign' as const;
  static readonly plural = 'latest-process-designs';
  static schema = latestProcessDesignSchema;

  // --- Static CRUD methods ---

  static async find(filter?: any): Promise<LatestProcessDesign[]> {
    const url = `${this.baseUrl}/${this.plural}`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const results = await this.get<any[]>(url, params);
    return results.map((data) => new LatestProcessDesign(data));
  }

  static async findOne(filter?: any): Promise<LatestProcessDesign | null> {
    const url = `${this.baseUrl}/${this.plural}/find-one`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const data = await this.get<any>(url, params);
    return data ? new LatestProcessDesign(data) : null;
  }

  static async findById(id: Id, filter?: any): Promise<LatestProcessDesign> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const data = await this.get<any>(url, params);
    return new LatestProcessDesign(data);
  }

  static async create(
    data: Omit<LatestProcessDesignData, 'id'>,
  ): Promise<LatestProcessDesign> {
    const url = `${this.baseUrl}/${this.plural}`;
    const result = await this.post<any>(url, data);
    return new LatestProcessDesign(result);
  }

  static async createMany(
    data: Omit<LatestProcessDesignData, 'id'>[],
  ): Promise<LatestProcessDesign[]> {
    const url = `${this.baseUrl}/${this.plural}/create-many`;
    const results = await this.post<any[]>(url, data);
    return results.map((item: any) => new LatestProcessDesign(item));
  }

  static async createWithChildren(
    data: LatestProcessDesignCreateWithChildrenData,
  ): Promise<LatestProcessDesign> {
    const url = `${this.baseUrl}/${this.plural}/create-with-children`;
    const result = await this.post<any>(url, data);
    return new LatestProcessDesign(result);
  }

  static async createManyWithChildren(
    data: LatestProcessDesignCreateWithChildrenData[],
  ): Promise<LatestProcessDesign[]> {
    const url = `${this.baseUrl}/${this.plural}/create-many-with-children`;
    const results = await this.post<any[]>(url, data);
    return results.map((item: any) => new LatestProcessDesign(item));
  }

  static async updateById(
    id: Id,
    updates: Partial<LatestProcessDesignData>,
  ): Promise<LatestProcessDesign> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    const result = await this.patch<any>(url, updates);
    return new LatestProcessDesign(result);
  }

  static async deleteById(id: Id): Promise<boolean> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    try {
      await this.del<void>(url);
      return true;
    } catch {
      return false;
    }
  }
}
